import React, { useEffect, useState } from "react";
import { Cake, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "../../../firebase/AuthProvider";

interface Resource { participantId: number; displayName: string; observanceDate: string }

// Names are never embedded in retained chat bodies/previews/searches. Resolve current access on demand.
export default function ParticipantBirthdayCard({ data }: { data: unknown }) {
  const { currentUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [resources, setResources] = useState<Resource[]>([]);
  const [resourceOwnerKey, setResourceOwnerKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const digestId = data && typeof data === "object" ? (data as { digestId?: unknown }).digestId : undefined;
  const ownerKey = currentUser && typeof digestId === "string" ? `${currentUser.uid}:${digestId}` : "";
  const visibleResources = ownerKey && resourceOwnerKey === ownerKey ? resources : [];
  useEffect(() => {
    if (!open || !currentUser || typeof digestId !== "string" || !/^[a-f0-9-]{36}$/i.test(digestId)) return;
    let active = true;
    let focused = true;
    let generation = 0;
    const controller = new AbortController();
    const refresh = async () => {
      if (!focused || document.visibilityState === "hidden") return;
      const requestGeneration = ++generation;
      setResources([]); setResourceOwnerKey(""); setLoading(true); setFailed(false);
      try {
        const token = await currentUser.getIdToken();
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/participants/birthday-digests/${digestId}`, {
          headers: { Authorization: `Bearer ${token}` }, cache: "no-store", signal: controller.signal,
        });
        if (!response.ok) throw new Error("Unavailable");
        const result = await response.json();
        if (active && focused && requestGeneration === generation && !document.hidden) { setResourceOwnerKey(ownerKey); setResources(Array.isArray(result.resources) ? result.resources : []); }
      } catch { if (active && focused && requestGeneration === generation) setFailed(true); }
      finally { if (active && focused && requestGeneration === generation) setLoading(false); }
    };
    const conceal = () => { focused = false; generation++; setResources([]); setResourceOwnerKey(""); setLoading(false); };
    const regainFocus = () => { focused = true; void refresh(); };
    const visibility = () => { if (document.visibilityState === "hidden") conceal(); else regainFocus(); };
    void refresh();
    const timer = window.setInterval(() => { if (document.visibilityState === "visible") void refresh(); }, 15000);
    window.addEventListener("focus", regainFocus);
    window.addEventListener("blur", conceal);
    document.addEventListener("visibilitychange", visibility);
    return () => { active = false; generation++; controller.abort(); window.clearInterval(timer); window.removeEventListener("focus", regainFocus); window.removeEventListener("blur", conceal); document.removeEventListener("visibilitychange", visibility); };
  }, [currentUser, digestId, open, ownerKey]);
  return <div className="min-w-[220px] max-w-sm text-slate-800 dark:text-slate-100">
    <button type="button" className="flex w-full items-center gap-2 rounded-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#7e57c2]" aria-expanded={open}
      onClick={() => { setResources([]); setResourceOwnerKey(""); setOpen(!open); }}><Cake className="h-5 w-5 text-[#7e57c2] dark:text-[#c4aff1]" aria-hidden />
      <span className="text-sm font-semibold">Participant birthdays</span><ChevronDown className="ml-auto h-4 w-4" aria-hidden /></button>
    {open && <div className="mt-3 text-sm" aria-live="polite">{loading ? "Checking access..." : failed ? "Reminder unavailable." : visibleResources.length === 0 ? "Birthday details are no longer available." :
      <ul className="space-y-2">{visibleResources.map(resource => <li key={resource.participantId}><Link className="font-medium text-[#7656b4] hover:underline dark:text-[#c4aff1]" to={`/participant/${resource.participantId}/activity`}>{resource.displayName}</Link></li>)}</ul>}</div>}
  </div>;
}
